import {
  Divider,
  FormHelperText,
  Slider,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ReactElement } from "react";

import {
  AVAILABLE_MODELS,
  AvailableModel,
  MODEL_TOKEN_LIMITS
} from "../../../services/claude";
import { PromptTemplateCreationParams } from "../../../types/prompt-template";
import { LensFormErrors } from "../utils";
import {
  TEMPERATURE_MAX,
  TEMPERATURE_MIN,
  TEMPERATURE_STEP
} from "../constants";
import FormSelectField from "./FormSelectField";

interface ModelConfigFormSectionProps {
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  availableModels: readonly typeof AVAILABLE_MODELS[number][];
  onModelConfigChange: (field: string, value: string | number) => void;
}

const getTokenHelperText = (
  model: string,
  availableModels: readonly typeof AVAILABLE_MODELS[number][]
): string => {
  const limits = MODEL_TOKEN_LIMITS[model as AvailableModel];
  const name = availableModels.find((m) => m.id === model)?.name ?? model;
  return limits
    ? `Maximum number of tokens the model will generate (up to ${limits.maxOutputTokens.toLocaleString()} for ${name})`
    : "Maximum number of tokens the model will generate";
};

const PROVIDER_OPTIONS = [{ value: "anthropic", label: "Anthropic" }];

const toModelOptions = (
  models: readonly typeof AVAILABLE_MODELS[number][]
): { value: string; label: string }[] =>
  models.map((m) => ({ value: m.id, label: m.name }));

const ModelConfigFormSection = ({
  formData, formErrors, availableModels, onModelConfigChange
}: ModelConfigFormSectionProps): ReactElement => {
  const modelOptions = toModelOptions(availableModels);

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Model Configuration</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormSelectField
          label="Provider"
          value={formData.model_config.provider}
          options={PROVIDER_OPTIONS}
          error={formErrors["model_config.provider"]}
          onChange={(v): void => onModelConfigChange("provider", v)}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormSelectField
          label="Model"
          value={formData.model_config.model}
          options={modelOptions}
          error={formErrors["model_config.model"]}
          onChange={(v): void => onModelConfigChange("model", v)}
        />
      </Grid>
      <TemperatureField formData={formData} formErrors={formErrors} onModelConfigChange={onModelConfigChange} />
      <MaxTokensField formData={formData} formErrors={formErrors} availableModels={availableModels} onModelConfigChange={onModelConfigChange} />
    </>
  );
};

interface TemperatureFieldProps {
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  onModelConfigChange: (field: string, value: number) => void;
}

const TemperatureField = ({ formData, formErrors, onModelConfigChange }: TemperatureFieldProps): ReactElement => (
  <Grid size={{ xs: 12 }}>
    <Typography variant="subtitle2">
      Temperature: {formData.model_config.temperature.toFixed(2)}
    </Typography>
    <Slider
      value={formData.model_config.temperature}
      min={TEMPERATURE_MIN}
      max={TEMPERATURE_MAX}
      step={TEMPERATURE_STEP}
      onChange={(_, value): void => onModelConfigChange("temperature", value as number)}
      aria-labelledby="temperature-slider"
      sx={{ mt: 1 }}
    />
    <FormHelperText>
      Lower values (closer to 0) make responses more deterministic. Higher values (closer to 1) make responses more creative.
    </FormHelperText>
    {formErrors["model_config.temperature"] && (
      <FormHelperText error>{formErrors["model_config.temperature"]}</FormHelperText>
    )}
  </Grid>
);

interface MaxTokensFieldProps {
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  availableModels: readonly typeof AVAILABLE_MODELS[number][];
  onModelConfigChange: (field: string, value: number) => void;
}

const MaxTokensField = ({ formData, formErrors, availableModels, onModelConfigChange }: MaxTokensFieldProps): ReactElement => (
  <Grid size={{ xs: 12 }}>
    <TextField
      fullWidth
      type="number"
      label="Max Tokens"
      value={formData.model_config.max_tokens}
      onChange={(e): void => onModelConfigChange("max_tokens", parseInt(e.target.value, 10))}
      error={!!formErrors["model_config.max_tokens"]}
      helperText={formErrors["model_config.max_tokens"] || getTokenHelperText(formData.model_config.model, availableModels)}
      required
      InputProps={{ inputProps: { min: 1 } }}
    />
  </Grid>
);

export default ModelConfigFormSection;
